import { useState, useEffect } from 'react';
import { useIntl } from 'react-intl';
import useFormInstance from 'antd/es/form/hooks/useFormInstance';
import dayjs from 'dayjs';
import { TooltipWithIcon } from 'ui';
import { Input } from '../../Input';
import { StyledNewListingForm } from './style';
import EnumSelect from '../../../admin/Selects/EnumSelect';
import { Checkbox } from '../../Checkbox';
import { DatePicker } from '../../DatePicker';
import { InputNumber } from '../../InputNumber';
import { Label } from 'ui/Label';
import useQueryApiClient from '../../../utils/useQueryApiClient';
import { routes } from '../../../config/config';
import { publicPath } from 'enums/publicRoutes';
import { Listing } from '../../../types/ListingData';
import { TextArea } from 'ui/TextArea';
import { WithoutModalAddressFinder } from 'ui/AdressSearch/WithoutModal';
import { Divider } from 'ui/Divider';

type TransactionType = 'purchase' | 'reservation' | 'dynamic_purchase';

interface GeneratedIdentifiers {
  purchase: string | null;
  reservation: string | null;
  dynamic_purchase: string | null;
}

interface IdentifierResponse {
  identifier: string;
}

interface NewListingFormProps {
  listingData?: Listing;
  listingId?: string | null;
  isEditing?: boolean;
  ignoreDeadlineValidation?: boolean;
}

export const NewListingForm = ({
  listingData,
  listingId,
  isEditing = false,
  ignoreDeadlineValidation = false,
}: NewListingFormProps) => {
  const [transactionType, setTransactionType] = useState<TransactionType | ''>(
    ''
  );
  const [isPrivate, setIsPrivate] = useState<boolean>(false);
  const [autoExtendPublication, setAutoExtendPublication] =
    useState<boolean>(false);
  const [publicationStartDate, setPublicationStartDate] =
    useState<dayjs.Dayjs | null>(null);
  const [generatedIdentifiers, setGeneratedIdentifiers] =
    useState<GeneratedIdentifiers>({
      purchase: null,
      reservation: null,
      dynamic_purchase: null,
    });

  const intl = useIntl();
  const form = useFormInstance();

  const { appendData: generateIdentifier } = useQueryApiClient({
    request: {
      url: '/api/v2/listings/generate-identifier',
      method: 'POST',
    },
    onSuccess: (
      data: IdentifierResponse,
      passOnSuccess: { type: TransactionType }
    ) => {
      setGeneratedIdentifiers((prev) => ({
        ...prev,
        [passOnSuccess.type]: data.identifier,
      }));
      form.setFieldValue('identifier', data.identifier);
    },
  });

  const generateIdentifierForType = (type: TransactionType) => {
    generateIdentifier({ transaction_type: type }, undefined, { type });
  };

  const handlePrivateLink = (checked: boolean) => {
    setIsPrivate(checked);
  };

  const handleTransactionTypeChange = (value: TransactionType) => {
    setTransactionType(value);
    form.setFieldValue('transaction_type', value);

    if (value && generatedIdentifiers[value as keyof GeneratedIdentifiers]) {
      form.setFieldValue(
        'identifier',
        generatedIdentifiers[value as keyof GeneratedIdentifiers]
      );
    } else if (value) {
      generateIdentifierForType(value);
    } else {
      form.setFieldValue('identifier', '');
    }
  };

  useEffect(() => {
    if (isEditing && listingData) {
      if (listingData.publication_start_date) {
        const startDate = dayjs(listingData.publication_start_date);
        setPublicationStartDate(startDate);
      }

      setIsPrivate(listingData.is_private || false);

      form.setFieldsValue({
        lks_lat: listingData.lks_lat ?? '',
        lks_long: listingData.lks_long ?? '',
      });

      const autoExtendValue = listingData.auto_extend_publication || false;

      setAutoExtendPublication(autoExtendValue);

      setTransactionType(listingData.transaction_type || '');

      if (listingData.transaction_type && listingData.identifier) {
        setGeneratedIdentifiers((prev) => ({
          ...prev,
          [listingData.transaction_type]: listingData.identifier,
        }));
      }
    } else if (!isEditing) {
      const now = dayjs();
      const currentMinute = now.minute();
      const minutesToSubtract = currentMinute % 10;
      const roundedTime = now
        .subtract(minutesToSubtract, 'minute')
        .second(0)
        .millisecond(0);
      setPublicationStartDate(roundedTime);
      form.setFieldValue('publication_start_date', roundedTime);

      handleTransactionTypeChange('purchase');
    }
  }, [isEditing, listingData, form]);

  useEffect(() => {
    if (publicationStartDate && !isEditing) {
      const endDate = publicationStartDate.add(7, 'days');
      form.setFieldValue('publication_end_date', endDate);
    }
  }, [publicationStartDate, form, isEditing]);

  useEffect(() => {
    if (listingId && isPrivate) {
      const privateLinkUrl = `${routes.api.frontendUrl}${publicPath.wholesaleListingDetail(listingId)}`;
      form.setFieldValue('private_link_full', privateLinkUrl);
    } else if (!listingId && isPrivate) {
      form.setFieldValue(
        'private_link_full',
        intl.formatMessage({ id: 'listings.private_link_placeholder' })
      );
    }
  }, [listingId, isPrivate, form, intl]);

  return (
    <StyledNewListingForm>
      <div className={'row'}>
        <div className={'name-title'}>
          <Input
            name={'title'}
            type={'text'}
            validations={'requiredText'}
            label={intl.formatMessage({ id: 'general.title' })}
          />
        </div>
        <div className={'identifier'}>
          <Input
            name={'identifier'}
            type={'text'}
            label={intl.formatMessage({ id: 'listings.identifier.long' })}
            readOnly={true}
          />
        </div>
      </div>

      <div className={'row'}>
        <div className={'transaction-type'}>
          <EnumSelect
            name={'transaction_type'}
            label={intl.formatMessage({ id: 'listings.transaction_type' })}
            validations={'required'}
            code={'ListingTransactionType'}
            onChange={handleTransactionTypeChange}
          />
        </div>
        {transactionType === 'reservation' && (
          <div className={'reservation-purchase-number'}>
            <Input
              name={'transaction_context'}
              label={intl.formatMessage({ id: 'listings.transaction_context' })}
              validations={
                transactionType === 'reservation' ? 'requiredText' : 'none'
              }
            />
          </div>
        )}
      </div>
      <div className={'row'}>
        <div className={'listing-description'}>
          <TextArea
            name={'description'}
            label={intl.formatMessage({ id: 'listings.listing_description' })}
            maxLength={2000}
          />
        </div>
      </div>
      <div className={'row'}>
        <Checkbox
          name={'is_private'}
          label={intl.formatMessage({ id: 'listings.private_listing' })}
          onChange={(e) => handlePrivateLink(e.target.checked)}
        />
        <Checkbox
          name={'single_producer_only'}
          label={intl.formatMessage({ id: 'listings.single_producer_only' })}
        />
      </div>
      {isPrivate && (
        <div className={'private_link'}>
          <Input
            name={'private_link_full'}
            label={intl.formatMessage({ id: 'general.link' })}
            readOnly={true}
          />
        </div>
      )}

      <Divider />

      <div className={'listing-publication-dates'}>
        <Label
          label={intl.formatMessage({ id: 'listings.publication_dates' })}
          title={true}
        />
        <div className={'row'}>
          <div className={'publication-start-date'}>
            <DatePicker
              name={'publication_start_date'}
              label={intl.formatMessage({
                id: 'listings.publication_start_date_time',
              })}
              format="DD.MM.YYYY HH:mm"
              validations={'required'}
              value={publicationStartDate || undefined}
              showTime={{ minuteStep: 10 }}
              needConfirm={false}
              disabledDate={
                ignoreDeadlineValidation
                  ? undefined
                  : (current) => {
                      if (!current) return false;
                      return current.isBefore(dayjs(), 'day');
                    }
              }
              disabledTime={
                ignoreDeadlineValidation
                  ? undefined
                  : (current) => {
                      if (!current) {
                        return {};
                      }
                      const now = dayjs();
                      const isToday = current.isSame(now, 'day');

                      if (!isToday) {
                        return {};
                      }

                      const currentHour = now.hour();
                      const currentMinute = now.minute();
                      const roundedMinute = Math.floor(currentMinute / 10) * 10;

                      return {
                        disabledHours: () => {
                          const hours = [];
                          for (let i = 0; i < currentHour; i++) {
                            hours.push(i);
                          }
                          return hours;
                        },
                        disabledMinutes: (selectedHour?: number) => {
                          if (selectedHour === currentHour) {
                            const minutes = [];
                            for (let i = 0; i < roundedMinute; i += 10) {
                              minutes.push(i);
                            }
                            return minutes;
                          }
                          return [];
                        },
                      };
                    }
              }
            />
          </div>
          <div className={'publication-end-date'}>
            <DatePicker
              name={'publication_end_date'}
              label={intl.formatMessage({
                id: 'listings.publication_end_date_time',
              })}
              format="DD.MM.YYYY HH:mm"
              validations={
                ignoreDeadlineValidation
                  ? ['required']
                  : ['required', 'endDateAfterStart']
              }
              showTime={{ minuteStep: 10 }}
              needConfirm={false}
            />
          </div>
        </div>
        <Checkbox
          name={'auto_extend_publication'}
          label={intl.formatMessage({ id: 'listings.auto_extend_publication' })}
          onChange={(e) => setAutoExtendPublication(e.target.checked)}
        />
        {autoExtendPublication && (
          <div className={'extend-days'}>
            <InputNumber
              name={'extend_days'}
              label={intl.formatMessage({ id: 'listings.extend_by' })}
              validations={'required'}
              controls={false}
              min={1}
              initialValue={7}
              suffix={intl.formatMessage({ id: 'general.days' })}
            />
          </div>
        )}
      </div>

      <Divider />
      <Label
        label={intl.formatMessage({ id: 'listings.delivery_radius' })}
        title={true}
      />
      <div className={'delivery-radius'}>
        <div className={'delivery-radius-km'}>
          <InputNumber
            name={'delivery_radius_km'}
            label={intl.formatMessage({ id: 'listings.delivery_radius_km' })}
            validations={['required']}
            controls={false}
            min={1}
            max={1000}
          />
        </div>
        <div className={'listing-address'}>
          <WithoutModalAddressFinder
            form={form}
            name="address"
            disabled={false}
            deliveryTypeValue="delivery"
            validations="required"
            requireCoordinates={true}
            label={
              <span>
                {intl.formatMessage({ id: 'listings.form.delivery_address' })}
                <TooltipWithIcon
                  title={intl.formatMessage({
                    id: 'listings.delivery_address_radius_note',
                  })}
                />
              </span>
            }
          />
        </div>
      </div>
    </StyledNewListingForm>
  );
};
